// AudioControlBar - Reusable timeline control bar component
import React, { useEffect } from 'react';
import { ActionIcon, Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlay,
  faPause,
  faStop,
  faCopy,
  faCut,
  faTrash,
  faSearchPlus,
  faSearchMinus,
  faMagic,
  faGripLinesVertical,
  faLocationCrosshairs
} from '@fortawesome/free-solid-svg-icons';
import styles from '../AudioEditor.module.css';

interface AudioControlBarProps {
  // Playback controls
  isPlaying: boolean;
  currentTime: number;
  totalDuration: number;
  hasTracks: boolean;
  onTogglePlayback: () => void;
  onStop: () => void;
  formatTime: (seconds: number) => string;
  
  // Track actions (optional - only shown when track is selected)
  selectedTrackId?: string | null;
  selectedTrack?: any;
  onDuplicateTrack?: (trackId: string) => void;
  onSplitTrack?: (trackId: string) => void;
  onDeleteTrack?: (trackId: string) => void;
  onFillGaps?: () => void; // NEW: Fill gaps handler
  totalTracks?: number; // NEW: Total number of tracks
  
  // Snap controls
  snapToGrid: boolean;
  gridSize: number;
  onSnapToggle: (enabled: boolean) => void;
  onGridSizeChange: (size: number) => void;
  
  // Zoom controls  
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;

  // Auto-center controls
  autoCenter?: boolean;
  onAutoCenterToggle?: (enabled: boolean) => void;
}

const AudioControlBar: React.FC<AudioControlBarProps> = ({
  isPlaying,
  currentTime,
  totalDuration,
  hasTracks,
  onTogglePlayback,
  onStop,
  formatTime,
  selectedTrackId,
  selectedTrack,
  onDuplicateTrack,
  onSplitTrack,
  onDeleteTrack,
  onFillGaps,
  totalTracks = 0,
  snapToGrid,
  gridSize,
  onSnapToggle,
  onGridSizeChange,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  autoCenter = false,
  onAutoCenterToggle
}) => {

  const [hideButtons, setHideButtons] = React.useState(false);
  const canSplitTrack = selectedTrack && 
    currentTime > selectedTrack.startTime && 
    currentTime < selectedTrack.startTime + selectedTrack.duration;
    useEffect(() => {
      if(!!selectedTrack){
        setHideButtons(false);
      }
    }, [selectedTrack]);

  return (
    <div className={styles.timelineControls}>
      {/* Playback Controls */}
      <div className={styles.playbackControls}>
        <ActionIcon
          size="lg"
          onClick={onTogglePlayback}
          color={isPlaying ? 'red' : 'blue'}
          disabled={!hasTracks}
        >
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        </ActionIcon>
        
        <ActionIcon
          size="lg"
          onClick={onStop}
          style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#f87171' }}
        >
          <FontAwesomeIcon icon={faStop} />
        </ActionIcon>
        
        <Text size="sm" className={styles.timeDisplay}>
          {formatTime(currentTime)} / {formatTime(totalDuration)}
        </Text>
      </div>

      {/* Track Actions and Controls */}
      <div className={styles.trackBtn2}>
        {/* Track Action Buttons - Only show when track is selected */}
        {!hideButtons && selectedTrackId && onDuplicateTrack && onSplitTrack && onDeleteTrack && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginRight: '15px', 
            paddingRight: '15px', 
            borderRight: '1px solid rgba(255, 255, 255, 0.1)' 
          }}>
            <ActionIcon
              onClick={() => { onDuplicateTrack(selectedTrackId), setHideButtons(true)}}
              title="Duplicate Track"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </ActionIcon>
            
            <ActionIcon
              onClick={() => { onSplitTrack(selectedTrackId), setHideButtons(true)}}
              disabled={!canSplitTrack}
              title="Split Track at Playhead"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canSplitTrack ? 1 : 0.5
              }}
            >
              <FontAwesomeIcon icon={faCut} />
            </ActionIcon>
            
            <ActionIcon
              onClick={() => onDeleteTrack(selectedTrackId)}
              title="Delete Track"
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#f87171',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </ActionIcon>
          </div>
        )}

        {/* Fill Gap Button - Show when there are multiple tracks */}
        {onFillGaps && totalTracks > 1 && (
          <ActionIcon
            onClick={onFillGaps}
            title="Fill gaps between audio tracks"
            style={{
              background: '#2d2d2d',
              color: 'white',
              borderRadius: '8px',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '15px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <FontAwesomeIcon icon={faGripLinesVertical} />
          </ActionIcon>
        )}

        {/* Snap Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text size="sm">Snap</Text>
          <input
            type="checkbox"
            checked={snapToGrid}
            onChange={(event) => onSnapToggle(event.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              backgroundColor: snapToGrid ? '#3b82f6' : 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          />
          <input
            type="number"
            value={gridSize}
            onChange={(event) => onGridSizeChange(Number(event.target.value) || 1)}
            min={0.1}
            max={10}
            step={0.1}
            disabled={!snapToGrid}
            style={{
              width: '70px',
              height: '32px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white',
              padding: '4px 8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
          <Text size="xs" color="dimmed">s</Text>
        </div>
        
        {/* Zoom Controls */}
        <div className={styles.zoomControls}>
          <ActionIcon onClick={onZoomOut} disabled={zoomLevel <= 0.1}>
            <FontAwesomeIcon icon={faSearchMinus} />
          </ActionIcon>
          <Text size="xs" style={{ minWidth: '45px', textAlign: 'center' }}>
            {Math.round(zoomLevel * 100)}%
          </Text>
          <ActionIcon onClick={onZoomIn} disabled={zoomLevel >= 10}>
            <FontAwesomeIcon icon={faSearchPlus} />
          </ActionIcon>
          <ActionIcon onClick={onResetZoom} title="Reset Zoom">
            <FontAwesomeIcon icon={faMagic} />
          </ActionIcon>
        </div>

        {/* Auto-Center Toggle Button */}
        <ActionIcon
          onClick={() => {
            if (onAutoCenterToggle) {
              onAutoCenterToggle(!autoCenter);
            }
          }}
          title={autoCenter ? "Disable auto-center playhead" : "Enable auto-center playhead"}
          style={{
            background: autoCenter ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
            color: autoCenter ? '#3b82f6' : 'white',
            border: autoCenter ? '2px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faLocationCrosshairs} />
        </ActionIcon>
      </div>
    </div>
  );
};

export default AudioControlBar;
